import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	LinearProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import moment from 'moment';

import {
	WalletOperation,
	WalletOperationResolutionAction,
	WalletOperationResolutionResult,
	isWalletOperationOpen,
} from 'lib/models/walletOperations';
import { price } from 'lib/numbers';

import useWalletOperation from 'hooks/useWalletOperation';
import useWalletOperationActions from 'hooks/useWalletOperationActions';
import useWalletOperationLabels from 'hooks/useWalletOperationLabels';

import ConfirmationDialog from 'components/ConfirmationDialog';
import TooltipButton from 'components/TooltipButton';

type Props = {
	/** The operation to open, or null when the dialog is closed. */
	operationId: string | null;
	onClose: () => void;
	/** Called after an action changed the record, so the listing behind stops lying. */
	onResolved: () => void;
};

/**
 * The longest reason the API records. A longer one is refused, so it is not offered.
 */
const MAX_REASON = 500;

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

function when(value?: string): string {
	return value ? moment(value).format(DATE_FORMAT) : '-';
}

type FieldProps = {
	label: string;
	value: string;
};

function Field({ label, value }: FieldProps) {
	return (
		<Box sx={{ minWidth: 200, paddingRight: 3, paddingBottom: 2 }}>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="body1">{value}</Typography>
		</Box>
	);
}

/**
 * Everything the operation record says, for the person who has to decide what happened, and
 * the three actions that decide it.
 *
 * It reads `GET wallet/operation/:id` rather than reusing the listing row, and the three
 * POSTs answer with the whole record, so an action re-renders this dialog from its own
 * response instead of reading the operation back.
 *
 * Every action needs a reason and the API refuses one without: the reason is what makes the
 * entry reviewable months later, which is the only thing the audit trail is for. So the
 * buttons stay disabled until the operator writes one, and they say that rather than just
 * being grey.
 */
export default function WalletOperationDetailModal({ operationId, onClose, onResolved }: Props) {
	const { t } = useTranslation();
	const { kindLabel, stateLabel, outcomeLabel, stageLabel, resolutionActionLabel, resolutionResultLabel } =
		useWalletOperationLabels();
	const { loading, operation, update } = useWalletOperation(operationId);
	const { loading: resolving, resolve } = useWalletOperationActions();

	const [reason, setReason] = useState<string>('');
	const [pending, setPending] = useState<WalletOperationResolutionAction | null>(null);

	// The API offers the three actions on exactly this: a record the reconciler gave up on.
	// Anything else is refused with `operation.not_in_review`, so it is said here first.
	const inReview = !!operation && operation.state === 'unresolved' && operation.stage === 'unresolved';

	// Another operator is holding the record, as the answer read it. It is a fact rather than
	// a guess, so the buttons say it instead of offering a click that can only be refused with
	// `operation.locked` — and the refusal is still handled below, because the lease may be
	// taken, or dropped, between this answer and the click.
	const locked = !!operation && operation.locked;

	// Why the three actions cannot be sent right now, which is what their tooltip says. An
	// empty string is "they can".
	const blocked = loading
		? t('common:loading')
		: !operation
			? t('common:walletOperationNotFound')
			: !inReview
				? t('common:walletOperationActionsNotInReview')
				: locked
					? t('common:walletOperationLockedHelp')
					: !reason.trim()
						? t('common:walletOperationReasonRequired')
						: '';

	// Whether the three actions are on offer at all. The reason is deliberately not part of
	// it: a missing reason is the one blocker the operator can clear from this screen, and
	// hiding the box they have to type in would be the reason it stays missing. Everything
	// else — a record that closed, one another operator is holding — can only be watched, so
	// the procedure, the reason and the buttons are not shown at all rather than shown dead.
	const actionable = !loading && !!operation && inReview && !locked;

	// The re-check is not held to the same rule as the other two. It decides nothing — it asks
	// the provider and does what the answer says — so the API offers it while the operation is
	// unresolved at all, whether or not the reconciler has given up, and without a reason. That
	// is the whole point: while the provider is slow, the operator can hurry the question along
	// instead of watching a customer's money hang.
	const canRecheck = !loading && !!operation && operation.state === 'unresolved' && !locked;

	// Its own refusals: a lock somebody else holds, and an operation whose request never
	// returned a provider handle, which has nothing to ask about. The API publishes the second
	// as `pollable`, so the button says why rather than answering `operation.not_pollable`.
	const recheckBlocked = loading
		? t('common:loading')
		: !operation
			? t('common:walletOperationNotFound')
			: operation.state !== 'unresolved'
				? t('common:walletOperationActionsNotInReview')
				: locked
					? t('common:walletOperationLockedHelp')
					: !operation.pollable
						? t('common:walletOperationNotPollableHelp')
						: '';

	function close() {
		setReason('');
		setPending(null);
		onClose();
	}

	async function onConfirm(confirmed: boolean) {
		const action = pending;
		setPending(null);

		if (!confirmed || !action || !operation) {
			return;
		}

		const resolved = await resolve(operation.id, action, reason.trim());

		if (!resolved) {
			return;
		}

		update(resolved.operation);
		setReason('');
		onResolved();
	}

	return (
		<Dialog open={!!operationId} onClose={close} maxWidth="md" fullWidth>
			<DialogTitle>
				{t('common:walletOperationDetailTitle', { id: operationId || '' })}
				<IconButton aria-label="close" onClick={close} sx={{ position: 'absolute', right: 8, top: 8 }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{!!loading && <LinearProgress />}

				{!loading && !operation && <Typography>{t('common:walletOperationNotFound')}</Typography>}

				{!!operation && (
					<>
						<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
							<Field label={t('common:walletOperationKind')} value={kindLabel(operation.kind)} />
							{isWalletOperationOpen(operation) && (
								<Field label={t('common:walletOperationStage')} value={stageLabel(operation.stage)} />
							)}
							<Field label={t('common:walletOperationState')} value={stateLabel(operation.state)} />
							<Field label={t('common:walletOperationOutcome')} value={outcomeLabel(operation.outcome)} />
							<Field label={t('common:walletOperationAmount')} value={price(operation.amount)} />
							<Field label={t('common:walletOperationSubject')} value={operation.subject} />
							<Field label={t('common:walletOperationActor')} value={operation.actor} />
							<Field label={t('common:walletOperationCreatedAt')} value={when(operation.createdAt)} />
							<Field label={t('common:walletOperationFirstSentAt')} value={when(operation.provider?.firstSentAt)} />
							<Field label={t('common:walletOperationLastAttemptAt')} value={when(operation.provider?.lastAttemptAt)} />
							<Field label={t('common:walletOperationEndpoint')} value={operation.provider?.endpoint || '-'} />
							<Field label={t('common:walletOperationLoadType')} value={operation.provider?.loadType || '-'} />
							<Field label={t('common:walletOperationAttempts')} value={`${operation.provider?.attempts ?? 0}`} />
							<Field label={t('common:walletOperationPolls')} value={`${operation.reconcile?.polls ?? 0}`} />
							<Field label={t('common:walletOperationNextPollAt')} value={when(operation.reconcile?.nextPollAt)} />
							<Field label={t('common:walletOperationOrderId')} value={operation.orderId || '-'} />
						</Box>

						<Box sx={{ paddingBottom: 2 }}>
							<Typography variant="caption" color="text.secondary">
								{t('common:walletOperationReason')}
							</Typography>
							<Typography variant="body1">
								{operation.reconcile?.reason || t('common:walletOperationReasonNone')}
							</Typography>
						</Box>

						<Divider sx={{ marginBottom: 2 }} />

						<Typography variant="subtitle1">{t('common:walletOperationProducts')}</Typography>

						{!operation.products.length && (
							<Typography variant="body2">{t('common:walletOperationNoProducts')}</Typography>
						)}

						{!!operation.products.length && (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>{t('common:product')}</TableCell>
										<TableCell>{t('common:quantity')}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{operation.products.map((product, index) => (
										<TableRow key={`${product.title}-${index}`}>
											<TableCell>{product.title}</TableCell>
											<TableCell>{product.quantity}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}

						<Divider sx={{ marginY: 2 }} />

						<Typography variant="subtitle1">{t('common:walletOperationIntent')}</Typography>
						<IntentAmounts operation={operation} />

						<Typography variant="caption" color="text.secondary">
							{t('common:walletOperationIntentRaw')}
						</Typography>
						<Box
							component="pre"
							sx={{
								backgroundColor: 'action.hover',
								padding: 2,
								overflowX: 'auto',
								fontSize: 12,
								margin: 0,
							}}
						>
							{JSON.stringify(operation.intent ?? {}, null, 2)}
						</Box>

						<Divider sx={{ marginY: 2 }} />

						<Typography variant="subtitle1">{t('common:walletOperationResolutions')}</Typography>

						<Resolutions
							operation={operation}
							actionLabel={resolutionActionLabel}
							resultLabel={resolutionResultLabel}
						/>

						<Divider sx={{ marginY: 2 }} />

						{actionable && (
							<Alert severity="info">
								<AlertTitle>{t('common:walletOperationProcedureTitle')}</AlertTitle>
								<Typography variant="body2">{t('common:walletOperationProcedureIntro')}</Typography>
								<Typography variant="body2">{t('common:walletOperationProcedureStep1')}</Typography>
								<Typography variant="body2">{t('common:walletOperationProcedureStep2')}</Typography>
								<Typography variant="body2">{t('common:walletOperationProcedureStep3')}</Typography>
							</Alert>
						)}

						{inReview && locked && (
							<Alert severity="warning" sx={{ marginTop: 2 }}>
								<AlertTitle>{t('common:walletOperationLocked')}</AlertTitle>
								<Typography variant="body2">{t('common:walletOperationLockedHelp')}</Typography>
							</Alert>
						)}

						{actionable && !operation.pollable && (
							<Alert severity="info" sx={{ marginTop: 2 }}>
								<AlertTitle>{t('common:walletOperationNotPollable')}</AlertTitle>
								<Typography variant="body2">{t('common:walletOperationNotPollableHelp')}</Typography>
							</Alert>
						)}

						{actionable && (
							<Alert severity="warning" sx={{ marginTop: 2 }}>
								<AlertTitle>{t('common:walletOperationActions')}</AlertTitle>
								<Typography variant="body2">{t('common:walletOperationReasonRequired')}</Typography>
							</Alert>
						)}

						{actionable && (
							<TextField
								value={reason}
								onChange={(event) => setReason(event.target.value.slice(0, MAX_REASON))}
								label={t('common:walletOperationReasonLabel')}
								helperText={t('common:walletOperationReasonHelp', {
									length: reason.trim().length,
									max: MAX_REASON,
								})}
								placeholder={t('common:walletOperationReasonPlaceholder')}
								disabled={!inReview || resolving}
								multiline
								minRows={2}
								fullWidth
								sx={{ marginTop: 2 }}
							/>
						)}
					</>
				)}
			</DialogContent>

			<DialogActions>
				{!!resolving && <LinearProgress sx={{ flex: 1 }} />}

				{canRecheck && (
					<TooltipButton title={recheckBlocked || t('common:walletOperationActionRecheckHelp')}>
						<Button disabled={!!recheckBlocked || resolving} onClick={() => setPending('recheck')}>
							{t('common:walletOperationActionRecheck')}
						</Button>
					</TooltipButton>
				)}

				{actionable && (
					<>
						<TooltipButton title={blocked || t('common:walletOperationActionResolveHelp')}>
							<Button disabled={!!blocked || resolving} onClick={() => setPending('resolve')}>
								{t('common:walletOperationActionResolve')}
							</Button>
						</TooltipButton>
						<TooltipButton title={blocked || t('common:walletOperationActionCompleteHelp')}>
							<Button disabled={!!blocked || resolving} variant="contained" onClick={() => setPending('settle')}>
								{t('common:walletOperationActionComplete')}
							</Button>
						</TooltipButton>
					</>
				)}

				<Button onClick={close}>{t('common:close')}</Button>
			</DialogActions>

			{!!pending && (
				<ConfirmationDialog
					title={resolutionActionLabel(pending)}
					description={
						reason.trim()
							? t('common:walletOperationActionConfirm', {
									action: resolutionActionLabel(pending),
									reason: reason.trim(),
								})
							: t('common:walletOperationActionConfirmNoReason', {
									action: resolutionActionLabel(pending),
								})
					}
					warningMessage={pending === 'settle' ? t('common:walletOperationActionCompleteWarning') : undefined}
					confirmButton={resolutionActionLabel(pending)}
					loading={resolving}
					onClose={onConfirm}
				/>
			)}
		</Dialog>
	);
}

type ResolutionsProps = {
	operation: WalletOperation;
	actionLabel: (action: WalletOperationResolutionAction) => string;
	resultLabel: (result: WalletOperationResolutionResult) => string;
};

/**
 * Every manual action taken on this operation, oldest first.
 *
 * It is the record of a person's decision about somebody's money — who, when, what they
 * did, why, and what came of it — so it is shown beside the operation rather than kept for
 * whoever thinks to read the database.
 */
function Resolutions({ operation, actionLabel, resultLabel }: ResolutionsProps) {
	const { t } = useTranslation();

	if (!operation.resolutions.length) {
		return <Typography variant="body2">{t('common:walletOperationNoResolutions')}</Typography>;
	}

	return (
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>{t('common:walletOperationResolutionAt')}</TableCell>
					<TableCell>{t('common:walletOperationResolutionAction')}</TableCell>
					<TableCell>{t('common:walletOperationResolutionBy')}</TableCell>
					<TableCell>{t('common:walletOperationResolutionResult')}</TableCell>
					<TableCell>{t('common:walletOperationResolutionReason')}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{operation.resolutions.map((resolution, index) => (
					<TableRow key={`${resolution.at}-${index}`}>
						<TableCell>{when(resolution.at)}</TableCell>
						<TableCell>{actionLabel(resolution.action)}</TableCell>
						<TableCell>{resolution.by}</TableCell>
						<TableCell>{resultLabel(resolution.result)}</TableCell>
						<TableCell sx={{ maxWidth: 320 }}>{resolution.reason}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}

type IntentAmountsProps = {
	operation: WalletOperation;
};

/**
 * The money of the intent, for the amount half of the matching procedure.
 *
 * Each figure is rendered only when the snapshot actually carries it: a refund's intent
 * and a purchase's are not the same shape, and an absent field is not a zero.
 */
function IntentAmounts({ operation }: IntentAmountsProps) {
	const { t } = useTranslation();
	const intent = operation.intent;

	if (!intent) return null;

	return (
		<Box sx={{ display: 'flex', flexWrap: 'wrap', paddingY: 1 }}>
			{typeof intent.total === 'number' && <Field label={t('common:total')} value={price(intent.total)} />}
			{typeof intent.amountPaid === 'number' && (
				<Field label={t('common:walletOperationAmountPaid')} value={price(intent.amountPaid)} />
			)}
			{typeof intent.pointsUsed === 'number' && (
				<Field label={t('common:walletOperationPoints')} value={`${intent.pointsUsed}`} />
			)}
			{typeof intent.paymentMethod === 'string' && (
				<Field label={t('common:walletOperationPaymentMethod')} value={intent.paymentMethod} />
			)}
			{typeof intent.code === 'string' && <Field label={t('common:code')} value={intent.code} />}
			{typeof intent.section === 'string' && (
				<Field label={t('common:walletOperationSection')} value={intent.section} />
			)}
			{(typeof intent.tableNumber === 'string' || typeof intent.tableNumber === 'number') && (
				<Field label={t('common:tableNumber')} value={`${intent.tableNumber}`} />
			)}
		</Box>
	);
}
